import React, { useRef } from 'react'
import dayjs from 'dayjs'
import classNames from 'classnames'
import { i18n } from '@/locale/i18n'
import useScoreChangeBurst from '@/components/ui/confetti/useScoreChangeBurst'
import {
  getMainSportsScores,
  getMatchStatusLabel,
  hasExtraTimeScore,
  isSportsMarketResolved,
  isSportsMatchLive,
  showPenaltiesScore,
  getMatchMinute,
  getSportsLeague,
} from '@/features/market/utils/sportsEvent'
import { getTeamSideImage } from '@/features/market/utils/teamFlag'
import Image from '@/components/ui/image/Image'
import Chip from '@/components/ui/chip'
import { categoryListUrl, keepQueryString } from '@/urls'

// Mobile sticky header: every size that changes between the full and compact
// states eases with the same curve so the collapse reads as one motion.
const MOTION = 'duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]'

// typography/label-md (14px) — team name, team code and kickoff time
const LABEL_MD =
  'text-[length:var(--typography-label-md-size)] leading-[var(--typography-label-md-line-height)] font-medium'

const LiveDot = () => (
  <span className="relative flex h-[7px] w-[7px] shrink-0" aria-hidden>
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-negative-4 opacity-60 [animation-duration:1.8s]" />
    <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-negative-4" />
  </span>
)

/**
 * Scoreboard header for the sports detail page: each side is a centered
 * column (crest above the team name), with the match state stacked in the
 * middle — status pill, then the score, or the kickoff date and time.
 * The centre column stacks the leaf-category tag, the date or score and the
 * kickoff time. The stats row and share/follow actions sit above the panel.
 *
 * `compact` is the scrolled state of the mobile sticky header: smaller crests,
 * score and padding, team names hidden (the short code stays), no category tag.
 */
const SportEventMatchHeader = ({ question, categories, compact = false }) => {
  const sportsData = question?.event_extra_data || {}
  const scoreRef = useRef(null)

  const isResolved = isSportsMarketResolved(question)
  const isLive = isSportsMatchLive(question) && !isResolved
  const showingPenalties = showPenaltiesScore(sportsData)
  const showingExtraTime = hasExtraTimeScore(sportsData)
  const mainScores = getMainSportsScores(sportsData)
  const hasScore = mainScores.home != null || mainScores.away != null
  const showingScore = isLive || (isResolved && hasScore)

  useScoreChangeBurst(scoreRef, {
    scores: {
      homeRegular: sportsData.home_score_regular_time,
      awayRegular: sportsData.away_score_regular_time,
      homeExtraTime: sportsData.home_score_extra_time,
      awayExtraTime: sportsData.away_score_extra_time,
      homePenalties: sportsData.home_score_penalties,
      awayPenalties: sportsData.away_score_penalties,
    },
    enabled: isLive,
  })

  const matchMinute = isLive ? getMatchMinute(sportsData) : null

  const statusLabel = showingPenalties
    ? i18n('question_card.sport_event.penalties')
    : showingExtraTime
      ? i18n('question_card.sport_event.extra_time')
      : getMatchStatusLabel(sportsData)

  let subScore = null
  if (showingPenalties) {
    subScore = `${sportsData.home_score_penalties ?? 0} - ${sportsData.away_score_penalties ?? 0}`
  } else if (showingExtraTime) {
    subScore = `${sportsData.home_score_extra_time ?? 0} - ${sportsData.away_score_extra_time ?? 0}`
  }

  const eventStartDate = question?.event_start_date
  const kickoffDate = eventStartDate ? dayjs(eventStartDate) : null
  const kickoffDay = kickoffDate
    ? kickoffDate.isSame(dayjs(), 'day')
      ? i18n('common.today')
      : kickoffDate.format('DD MMM')
    : null
  const kickoffTime = kickoffDate ? kickoffDate.format('hh:mm A') : null
  const leafCategory = categories?.length ? categories[categories.length - 1] : null
  const league = getSportsLeague(sportsData)

  const renderSide = (side, code, name) => (
    <div
      className={classNames(
        `flex min-w-0 flex-col items-center transition-[gap] ${MOTION}`,
        compact ? 'gap-1' : 'gap-2.5 md:gap-3'
      )}>
      <Image
        src={getTeamSideImage(question, side)}
        alt={name || code || ''}
        className={classNames(
          `flex shrink-0 items-center justify-center transition-[height,width] ${MOTION}`,
          compact ? 'h-9 w-9' : 'h-[88px] w-[88px] md:h-24 md:w-24'
        )}
        imageClassName="h-full! w-auto! max-w-full object-contain!"
        animate={false}
        loading="eager"
        fetchPriority="high"
        decoding="sync"
      />
      <div className="w-full min-w-0 text-center">
        {/* The name collapses (height + opacity) instead of unmounting, so the
            crest and code glide up rather than jump when the header compacts. */}
        <div
          className={classNames(
            `overflow-hidden transition-[max-height,opacity] ${MOTION}`,
            compact && code ? 'max-h-0 opacity-0' : 'max-h-9 opacity-100 md:max-h-12'
          )}>
          <div className="line-clamp-2 break-words text-sm font-bold leading-tight text-Neutral-6 md:text-lg">
            {name || code || 'TBD'}
          </div>
        </div>
        {code && (
          <div className={classNames(LABEL_MD, 'text-Neutral-4', compact ? 'font-bold' : 'mt-0.5')}>
            {code}
          </div>
        )}
      </div>
    </div>
  )

  const renderStatusPill = () => {
    if (isLive) {
      return (
        <span className="inline-flex items-center gap-[5px] rounded-full bg-negative-1 px-[9px] py-[3px] text-[11px] font-medium uppercase leading-normal tracking-wide text-negative-4">
          <LiveDot />
          {statusLabel}
          {matchMinute && <span className="tabular-nums font-semibold">{matchMinute}</span>}
        </span>
      )
    }

    if (isResolved && hasScore) {
      return (
        <span className="inline-flex items-center rounded-full bg-[var(--color-brand-04)] px-[10px] py-[3px] text-[11px] font-medium uppercase leading-normal tracking-wide text-[var(--color-contrast-01)]">
          {i18n('question_card.sport_event.full_time')}
        </span>
      )
    }

    return null
  }

  return (
    <div
      className={classNames(
        `flex w-full items-center bg-surface-3 bg-[linear-gradient(to_bottom,var(--color-surface-3)_35%,var(--color-surface-2)_100%)] transition-[min-height,padding] ${MOTION} md:block md:min-h-0 md:rounded-[var(--radius-sm)] md:bg-none md:p-6`,
        compact ? 'min-h-[76px] px-4 py-2' : 'min-h-[250px] p-4'
      )}>
      <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3 md:gap-6">
        {renderSide('home', sportsData.home_team_short_code, sportsData.home_team)}

        <div
          className={classNames(
            `flex min-w-[88px] flex-col items-center px-1 transition-[gap] ${MOTION} md:min-w-[140px] md:px-2`,
            compact ? 'gap-1' : 'gap-3 md:gap-2'
          )}>
          {(leafCategory?.title || league) && (
            <div
              className={classNames(
                `flex flex-col items-center overflow-hidden transition-[max-height,opacity] ${MOTION}`,
                compact ? 'invisible max-h-0 opacity-0' : league && leafCategory?.title ? 'max-h-16 opacity-100' : 'max-h-10 opacity-100'
              )}
              aria-hidden={compact}>
              {leafCategory?.title && (
                <Chip text={leafCategory.title} to={keepQueryString(categoryListUrl(categories))} />
              )}
              {league && (
                <div
                  className={classNames(
                    'max-w-[9.5rem] truncate text-center text-xs font-medium text-Neutral-4',
                    leafCategory?.title && 'mt-1'
                  )}
                  title={league}>
                  {league}
                </div>
              )}
            </div>
          )}

          {renderStatusPill()}

          {showingScore ? (
            <>
              <div
                ref={scoreRef}
                className={classNames(
                  `flex items-baseline gap-3 font-bold leading-none tabular-nums text-Neutral-6 transition-[font-size] ${MOTION}`,
                  compact ? 'text-[28px]' : 'text-[48px] md:text-[56px]'
                )}>
                <span>{mainScores.home ?? 0}</span>
                <span>-</span>
                <span>{mainScores.away ?? 0}</span>
              </div>
              {subScore && <div className="tabular-nums text-xs text-Neutral-4">{subScore}</div>}
            </>
          ) : (
            <>
              <div
                className={classNames(
                  `whitespace-nowrap font-bold leading-tight text-Neutral-6 transition-[font-size] ${MOTION}`,
                  compact ? 'text-lg' : 'text-[28px] md:text-[32px]'
                )}>
                {kickoffDay || 'vs'}
              </div>
              {kickoffTime && (
                <div className={`whitespace-nowrap ${LABEL_MD} text-Neutral-4`}>{kickoffTime}</div>
              )}
            </>
          )}
        </div>

        {renderSide('away', sportsData.away_team_short_code, sportsData.away_team)}
      </div>
    </div>
  )
}

export default SportEventMatchHeader
